import React, { useCallback, useState } from 'react';
import { Paper, Stack, Text, ThemeIcon, Group, Alert, Pagination, Loader, Select, SegmentedControl, Tabs, Button } from '@mantine/core';
import { IconMessageCircle, IconAlertCircle, IconSortAscending, IconSortDescending, IconClock, IconCheck, IconX, IconArchive, IconInfoCircle, IconArrowsShuffle } from '@tabler/icons-react';
import type { MentionReviewDto } from '@/domain/entities/MentionReview';
import { BusinessMentionsListProps } from './BusinessMentionsList.types';
import { BusinessMentionCard } from './BusinessMentionCard';
import { useMentionsForReview, useApproveMention, useRejectMention } from '@/application/queries/useMentionReview';
import { useInactiveMentionsQuery } from '@/application/queries/businessQueries';
import { BulkReassignMentionsModal } from '@/presentation/components/admin/MentionReview/BulkReassignMentionsModal';

/**
 * BusinessMentionsList Component
 *
 * Displays expandable mention cards for a specific business with inline review capabilities
 *
 * Features:
 * - Fetches mentions using MentionReview API with businessId filter
 * - Displays mentions in expandable card layout (single expansion)
 * - Inline approve/reject forms with admin notes
 * - Full confidence breakdown and Reddit context
 * - Filterable by review status (Pending, Approved, Rejected, All) - defaults to Pending
 * - Sortable by confidence score (default) or date with direction toggle
 * - Loading, error, and empty states
 *
 * Usage:
 * ```tsx
 * <BusinessMentionsList
 *   businessId="123"
 *   businessName="Test Coffee Shop"
 * />
 * ```
 */
export const BusinessMentionsList: React.FC<BusinessMentionsListProps> = ({
  businessId,
  businessName,
  businessRegionId,
  allowBulkSelection = false
}): React.ReactElement => {
  // State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [expandedMentionId, setExpandedMentionId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>('Confidence');
  const [sortDirection, setSortDirection] = useState<'Asc' | 'Desc'>('Desc');
  const [reviewStatus, setReviewStatus] = useState<string>('Pending');

  // Sprint 066: Bulk selection state
  // `selectedMentions` holds full DTOs (not just IDs) so the modal can render the
  // reviewed-mention warning banner without round-trips even when selected mentions
  // are on other pages or tabs than the one currently visible.
  const [selectedMentions, setSelectedMentions] = useState<Map<string, MentionReviewDto>>(
    new Map()
  );
  const [bulkModalOpen, setBulkModalOpen] = useState<boolean>(false);

  // Determine if we're on the Inactive tab
  const isInactiveTab = reviewStatus === 'Inactive';

  // Fetch active mentions using review API with businessId filter
  const {
    data: result,
    isLoading,
    error
  } = useMentionsForReview({
    businessId,
    page: currentPage,
    pageSize: 10,
    sortBy,
    sortDirection,
    reviewStatus: reviewStatus === 'All' ? undefined : reviewStatus
  }, {
    enabled: !isInactiveTab // Only fetch when not on Inactive tab
  });

  // Fetch inactive mentions (SESSION_092 - Sprint 011a: Task 11)
  const {
    data: inactiveResult,
    isLoading: isLoadingInactive,
    error: inactiveError
  } = useInactiveMentionsQuery(
    businessId,
    currentPage,
    10,
    isInactiveTab // Only fetch when on Inactive tab
  );

  // Mutations
  const approveMutation = useApproveMention();
  const rejectMutation = useRejectMention();

  /**
   * Handle toggle expand/collapse for a mention
   */
  const handleToggleExpand = (mentionId: string): void => {
    setExpandedMentionId((prev) => (prev === mentionId ? null : mentionId));
  };

  /**
   * Handle approve mutation
   */
  const handleApprove = async (mentionId: string, adminNotes?: string): Promise<void> => {
    // Find the mention to get businessIds from associatedBusinesses
    const mentions = isInactiveTab ? inactiveResult?.items : result?.items;
    const mention = mentions?.find(m => m.id === mentionId);

    if (!mention) {
      console.error('Mention not found for approval');
      return;
    }

    // Extract businessIds - for MentionReviewDto use associatedBusinesses, for InactiveMentionItem use businessId prop
    const businessIds = 'associatedBusinesses' in mention
      ? mention.associatedBusinesses.map(b => b.businessId)
      : [businessId]; // Inactive mentions - use the business this list is filtered by

    await approveMutation.mutateAsync({
      id: mentionId,
      request: { businessIds, adminNotes }
    });
    // Collapse card after approval
    setExpandedMentionId(null);
  };

  /**
   * Handle reject mutation
   */
  const handleReject = async (
    mentionId: string,
    rejectionReason: string,
    adminNotes?: string
  ): Promise<void> => {
    await rejectMutation.mutateAsync({
      id: mentionId,
      request: { rejectionReason, adminNotes }
    });
    // Collapse card after rejection
    setExpandedMentionId(null);
  };

  /**
   * Handle page change
   */
  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
    // Collapse all cards when changing page
    setExpandedMentionId(null);
  };

  /**
   * Sprint 066: Toggle a single mention's selection state.
   * Stores the full DTO on selection so the modal can later count reviewed mentions
   * without re-fetching.
   */
  const handleSelectionChange = useCallback(
    (mentionId: string, selected: boolean): void => {
      setSelectedMentions((prev) => {
        const next = new Map(prev);
        if (selected) {
          // Look up the mention on the current page — guarantee the DTO is present
          // when the user checks the box.
          const source = result?.items.find((m) => m.id === mentionId);
          if (source) {
            next.set(mentionId, source as MentionReviewDto);
          }
        } else {
          next.delete(mentionId);
        }
        return next;
      });
    },
    [result]
  );

  /**
   * Sprint 066: Select every mention currently visible on this page.
   * Inactive-tab mentions use a different DTO shape and are excluded from bulk ops.
   */
  const handleSelectAllVisible = useCallback((): void => {
    if (isInactiveTab) return;
    setSelectedMentions((prev) => {
      const next = new Map(prev);
      for (const mention of result?.items ?? []) {
        next.set(mention.id, mention as MentionReviewDto);
      }
      return next;
    });
  }, [isInactiveTab, result]);

  /**
   * Sprint 066: Clear all selected mentions (toolbar "Clear" button and
   * post-success handler).
   */
  const handleClearSelection = useCallback((): void => {
    setSelectedMentions(new Map());
  }, []);

  /**
   * Handle sort field change
   */
  const handleSortByChange = (value: string | null): void => {
    if (value) {
      setSortBy(value);
      setCurrentPage(1);
      setExpandedMentionId(null);
    }
  };

  /**
   * Handle sort direction change
   */
  const handleSortDirectionChange = (value: string): void => {
    setSortDirection(value as 'Asc' | 'Desc');
    setCurrentPage(1);
    setExpandedMentionId(null);
  };

  /**
   * Handle review status filter change
   */
  const handleReviewStatusChange = (value: string | null): void => {
    if (value) {
      setReviewStatus(value);
      setCurrentPage(1);
      setExpandedMentionId(null);
    }
  };

  // Compute values based on active or inactive tab
  const currentResult = isInactiveTab ? inactiveResult : result;
  const currentIsLoading = isInactiveTab ? isLoadingInactive : isLoading;
  const currentError = isInactiveTab ? inactiveError : error;

  const mentions = currentResult?.items || [];
  const totalCount = currentResult?.totalCount || 0;
  const totalPages = currentResult?.totalPages || 1;
  const actionsDisabled = approveMutation.isPending || rejectMutation.isPending;

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Group gap="sm">
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconMessageCircle size={22} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text fw={600} size="lg">
                Mentions
              </Text>
              <Text size="sm" c="dimmed">
                {isLoading
                  ? 'Loading mentions...'
                  : `${totalCount} total mention${totalCount !== 1 ? 's' : ''} for ${businessName}`}
              </Text>
            </Stack>
          </Group>
          <Group gap="sm" align="flex-end">
            <Select
              label="Sort by"
              value={sortBy}
              onChange={handleSortByChange}
              data={[
                { value: 'Confidence', label: 'Confidence Score' },
                { value: 'CreatedAt', label: 'Date Posted' }
              ]}
              size="sm"
              style={{ width: '160px' }}
            />
            <Stack gap={4}>
              <Text size="xs" fw={500}>
                Direction
              </Text>
              <SegmentedControl
                value={sortDirection}
                onChange={handleSortDirectionChange}
                data={[
                  {
                    value: 'Desc',
                    label: (
                      <Group gap={4} wrap="nowrap">
                        <IconSortDescending size={16} />
                        <Text size="sm">Desc</Text>
                      </Group>
                    )
                  },
                  {
                    value: 'Asc',
                    label: (
                      <Group gap={4} wrap="nowrap">
                        <IconSortAscending size={16} />
                        <Text size="sm">Asc</Text>
                      </Group>
                    )
                  }
                ]}
                size="sm"
              />
            </Stack>
          </Group>
        </Group>

        {/* Review Status Filter Tabs */}
        <Tabs value={reviewStatus} onChange={handleReviewStatusChange}>
          <Tabs.List>
            <Tabs.Tab value="Pending" leftSection={<IconClock size={16} />}>
              Pending Review
            </Tabs.Tab>
            <Tabs.Tab value="Approved" leftSection={<IconCheck size={16} />}>
              Approved
            </Tabs.Tab>
            <Tabs.Tab value="Rejected" leftSection={<IconX size={16} />}>
              Rejected
            </Tabs.Tab>
            <Tabs.Tab value="All">All</Tabs.Tab>
            <Tabs.Tab value="Inactive" leftSection={<IconArchive size={16} />}>
              Inactive ({isInactiveTab ? (currentIsLoading ? '...' : totalCount) : inactiveResult?.totalCount || 0})
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {/* Info Alert for Inactive Tab */}
        {isInactiveTab && (
          <Alert icon={<IconInfoCircle size={16} />} color="blue" title="Inactive Mentions">
            These mentions previously matched this business but no longer meet the current matching criteria.
            They are hidden from your main mentions list but preserved here for historical reference.
          </Alert>
        )}

        {/* Error Alert */}
        {currentError && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error Loading Mentions" color="red">
            {currentError instanceof Error ? currentError.message : 'Failed to load mentions'}
          </Alert>
        )}

        {/* Loading State */}
        {currentIsLoading && (
          <Stack align="center" py="xl">
            <Loader size="lg" />
            <Text c="dimmed">Loading {isInactiveTab ? 'inactive' : ''} mentions...</Text>
          </Stack>
        )}

        {/* Empty State */}
        {!currentIsLoading && !currentError && mentions.length === 0 && (
          <Alert icon={<IconMessageCircle size={16} />} title="No Mentions Found" color="blue">
            {isInactiveTab
              ? 'No inactive mentions for this business.'
              : `No mentions found for ${businessName}`}
          </Alert>
        )}

        {/* Sprint 066: Sticky bulk-action toolbar */}
        {allowBulkSelection && !isInactiveTab && selectedMentions.size > 0 && (
          <Paper
            p="sm"
            radius={0}
            withBorder
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: 'var(--mantine-color-gray-0)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
            }}
          >
            <Group justify="space-between">
              <Group gap="sm">
                <Text fw={700} c="blue">
                  {selectedMentions.size} selected
                </Text>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={handleSelectAllVisible}
                  disabled={actionsDisabled}
                >
                  Select all on this page
                </Button>
                <Button
                  variant="subtle"
                  size="xs"
                  color="gray"
                  onClick={handleClearSelection}
                  disabled={actionsDisabled}
                >
                  Clear
                </Button>
              </Group>
              <Button
                color="blue"
                leftSection={<IconArrowsShuffle size={16} />}
                onClick={(): void => setBulkModalOpen(true)}
                disabled={actionsDisabled}
              >
                Reassign selected…
              </Button>
            </Group>
          </Paper>
        )}

        {/* Mention Cards */}
        {!currentIsLoading && !currentError && mentions.length > 0 && (
          <Stack gap="md">
            {mentions.map((mention) => (
              <BusinessMentionCard
                key={mention.id}
                mention={mention as MentionReviewDto}
                isExpanded={expandedMentionId === mention.id}
                onToggleExpand={handleToggleExpand}
                // Don't pass approve/reject handlers for inactive mentions (read-only)
                {...(!isInactiveTab && {
                  onApprove: handleApprove,
                  onReject: handleReject
                })}
                actionsDisabled={actionsDisabled}
                showSelection={allowBulkSelection && !isInactiveTab}
                isSelected={selectedMentions.has(mention.id)}
                onSelectionChange={handleSelectionChange}
              />
            ))}
          </Stack>
        )}

        {/* Pagination */}
        {!currentIsLoading && !currentError && totalPages > 1 && (
          <Group justify="center">
            <Pagination total={totalPages} value={currentPage} onChange={handlePageChange} />
          </Group>
        )}
      </Stack>

      {/* Sprint 066: Bulk reassign modal */}
      {allowBulkSelection && (
        <BulkReassignMentionsModal
          isOpen={bulkModalOpen}
          onClose={(): void => setBulkModalOpen(false)}
          sourceBusinessId={businessId}
          sourceBusinessName={businessName}
          regionId={businessRegionId}
          selectedMentions={Array.from(selectedMentions.values())}
          onSuccess={(): void => {
            setSelectedMentions(new Map());
            setBulkModalOpen(false);
          }}
        />
      )}
    </Paper>
  );
};
